// Một dòng của trang Lịch sử — dùng chung cho cả năm nguồn.
//
// Tag (Luận giải / Đàm đạo) đứng ngay đầu dòng meta, trước nhãn loại hình: nó là thứ
// người xem quét mắt tìm, và cũng là thứ bộ lọc phía trên đang nói tới.
import { Link } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, MoreHorizontal, Pencil, Trash2, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import {
  IconAmDuong,
  IconBoiLive,
  IconChiTay,
  IconChomSao,
  IconDoanGio,
  IconDongXu,
  IconTuTru,
} from "@/components/nav-icons";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { renameSession } from "@/lib/boide-api";
import { formatHistoryDate } from "@/lib/history-date";
import {
  CATEGORY_LABELS,
  TYPE_LABELS,
  deleteFeedItem,
  deleteWarningFor,
  relatedQueryKeys,
  type FeedItem,
  type FeedType,
} from "@/lib/history-feed";
import { cn } from "@/lib/utils";

const TYPE_ICONS: Record<FeedType, (props: { className?: string }) => React.ReactElement> = {
  iching: IconDongXu,
  tuvi: IconChomSao,
  bazi: IconTuTru,
  palm: IconChiTay,
  tong_hop: IconAmDuong,
  live: IconBoiLive,
  birth_time: IconDoanGio,
};

/**
 * Đường mở lại một bản ghi.
 *
 * ⚠️ Bốn cách mở khác nhau, và chọn nhầm là mất dữ liệu trước mắt người xem:
 * - Phiên chỉ tay mở bằng `?reading=` (id BÀI LUẬN): ảnh bàn tay + vùng khoanh nằm ở
 *   bảng riêng, vào bằng `?session=` là mất sạch ảnh.
 * - Lá số mở bằng `?chart=`: `?session=` là phiên đàm đạo mở ra SAU lá số, mà phần lớn
 *   lá số chưa từng có phiên nào.
 * - Phiên Bói Đê theo chủ đề mở thẳng trang chủ đề.
 * - Lượt đoán giờ sinh mở `/doan-gio-sinh?probe=` — trang ấy tải lại kết quả đầy đủ
 *   rồi bật lớp trắc nghiệm ở màn kết quả, để còn phỏng vấn thêm được trên lượt cũ.
 */
function linkPropsFor(item: FeedItem) {
  if (item.source === "tuvi_chart") return { to: "/tu-vi" as const, search: { chart: item.id } };
  if (item.source === "bazi_chart") return { to: "/bat-tu" as const, search: { chart: item.id } };
  if (item.source === "palm_reading" || item.palmReadingId) {
    // Lượt chỉ tay chưa luận xong mở bằng `?draft=` để đi tiếp đúng chỗ đang dở;
    // `?reading=` chỉ dùng cho bài đã có. Nháp không có bài luận nào để tải.
    if (item.palmDraft) return { to: "/boi-chi-tay" as const, search: { draft: item.id } };
    return {
      to: "/boi-chi-tay" as const,
      search: { reading: item.palmReadingId ?? item.id },
    };
  }
  if (item.source === "live_session") {
    return { to: "/boi-live/$sessionId" as const, params: { sessionId: item.id } };
  }
  if (item.source === "birth_time_probe") {
    return { to: "/doan-gio-sinh" as const, search: { probe: item.id } };
  }
  if (item.boiDeSlug) {
    return {
      to: "/tong-hop/$topic" as const,
      params: { topic: item.boiDeSlug },
      search: { session: item.id },
    };
  }
  const to =
    item.type === "iching"
      ? ("/kinh-dich" as const)
      : item.type === "tuvi"
        ? ("/tu-vi" as const)
        : item.type === "bazi"
          ? ("/bat-tu" as const)
          : ("/tong-hop" as const);
  return { to, search: { session: item.id } };
}

export function HistoryFeedRow({ item }: { item: FeedItem }) {
  const qc = useQueryClient();
  const Icon = TYPE_ICONS[item.type];

  const [editing, setEditing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [name, setName] = useState(item.title);

  const rename = useMutation({
    mutationFn: () => renameSession(item.id, name.trim()),
    onSuccess: () => {
      setEditing(false);
      toast.success("Đã đổi tên phiên");
      void qc.invalidateQueries({ queryKey: ["history-feed"] });
      void qc.invalidateQueries({ queryKey: ["boide-sessions"] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const del = useMutation({
    mutationFn: () => deleteFeedItem(item),
    onSuccess: () => {
      toast.success("Đã xoá");
      setDeleteOpen(false);
      relatedQueryKeys(item).forEach((key) => {
        void qc.invalidateQueries({ queryKey: [key] });
      });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (editing) {
    return (
      <li className="rounded-lg border border-gold-soft/40 bg-background/40 backdrop-blur">
        <div className="flex items-center gap-2 p-3">
          <label htmlFor={`feed-name-${item.key}`} className="sr-only">
            Tên phiên tham vấn
          </label>
          <Input
            id={`feed-name-${item.key}`}
            value={name}
            autoFocus
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && name.trim()) rename.mutate();
              if (event.key === "Escape") setEditing(false);
            }}
          />
          <Button
            size="icon"
            variant="ghost"
            disabled={!name.trim() || rename.isPending}
            onClick={() => rename.mutate()}
            className="text-primary"
            aria-label="Lưu tên phiên"
            title="Lưu"
          >
            <Check />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={() => setEditing(false)}
            aria-label="Huỷ đổi tên"
            title="Huỷ"
          >
            <X />
          </Button>
        </div>
      </li>
    );
  }

  return (
    <li
      className={cn(
        "group rounded-lg border border-gold-soft/40 bg-background/40 backdrop-blur transition hover:border-primary/60 hover:shadow-gold",
        del.isPending && "opacity-50",
      )}
    >
      <div className="flex items-center">
        <Link {...linkPropsFor(item)} className="flex min-w-0 flex-1 items-center gap-3 p-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-xl border border-gold-soft/45 bg-primary/[0.09] text-primary shadow-[0_6px_16px_rgba(123,82,18,0.08)]">
            <Icon className="size-[21px]" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="break-words font-serif-soft text-[15px] leading-snug text-foreground/95">
              {item.title}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] uppercase tracking-wider text-muted-foreground sm:text-[11px]">
              <span
                className={cn(
                  "rounded-full border px-2 py-px text-[9px] font-medium sm:text-[10px]",
                  item.category === "luan-giai"
                    ? "border-primary/45 bg-primary/10 text-primary"
                    : "border-border bg-muted/40 text-muted-foreground",
                )}
              >
                {CATEGORY_LABELS[item.category]}
              </span>
              <span className="text-primary">{TYPE_LABELS[item.type]}</span>
              {item.details.map((detail) => (
                <span key={detail} className="normal-case">
                  {detail}
                </span>
              ))}
              <time dateTime={item.createdAt}>{formatHistoryDate(item.createdAt)}</time>
            </div>
          </div>
        </Link>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="mr-2 shrink-0 rounded-full"
              disabled={del.isPending}
              aria-label={`Thao tác cho ${item.title}`}
              title="Thao tác"
            >
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-40">
            <DropdownMenuGroup>
              {/* Chỉ phiên trò chuyện mới đổi tên được — bốn nguồn còn lại lấy tên từ
                  chính dữ liệu (họ tên trên lá số, tên người trong bài chỉ tay). */}
              {item.renamable && (
                <DropdownMenuItem
                  onSelect={() => {
                    setName(item.title);
                    setEditing(true);
                  }}
                >
                  <Pencil />
                  Đổi tên
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                disabled={del.isPending}
                onSelect={() => setDeleteOpen(true)}
              >
                <Trash2 />
                {del.isPending ? "Đang xoá..." : "Xoá"}
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <AlertDialogContent className="w-[calc(100vw-1.5rem)] rounded-2xl sm:max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle>Xoá mục này?</AlertDialogTitle>
              <AlertDialogDescription>{deleteWarningFor(item)}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Giữ lại</AlertDialogCancel>
              <AlertDialogAction
                disabled={del.isPending}
                onClick={(event) => {
                  // Dialog tự đóng khi bấm Action; giữ nó mở tới khi máy chủ trả lời để
                  // người xem thấy được lỗi thay vì tưởng đã xoá xong.
                  event.preventDefault();
                  del.mutate();
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {del.isPending ? "Đang xoá..." : "Xoá"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </li>
  );
}
